/**
 * Renders the value picker for a condition row by dispatching on
 * `descriptor.valueKind`.
 */

import type { FC } from 'react';

import {
  CountryCodesPicker,
  EnumListPicker,
  StringListPicker,
  TimeOfDayHint,
  WeekdaysPicker,
} from '../value-pickers';
import type {
  ConditionDescriptor,
  ConditionRowState,
} from '../model';

export interface ConditionRowValueSlotProps {
  row: ConditionRowState;
  onChange: (values: readonly string[]) => void;
  onTimeZoneChange?: (ianaId: string) => void;
}

function pickerLabel(descriptor: ConditionDescriptor): string {
  return `${descriptor.label} values`;
}

const ConditionRowValueSlot: FC<ConditionRowValueSlotProps> = ({
  row,
  onChange,
  onTimeZoneChange,
}) => {
  const { descriptor, values } = row;
  const ariaLabel = pickerLabel(descriptor);

  if (descriptor.valueKind === 'flag') {
    return (
      <span className="text-[11px] text-muted-foreground">
        No value required for this rule.
      </span>
    );
  }
  if (descriptor.valueKind === 'time-of-day') {
    return <TimeOfDayHint />;
  }
  if (descriptor.valueKind === 'unsupported') {
    return (
      <span className="text-[11px] text-muted-foreground">
        Read-only in this editor — preserved when you save.
      </span>
    );
  }
  if (descriptor.valueKind === 'enum-list' && descriptor.enumValues) {
    return (
      <EnumListPicker
        options={descriptor.enumValues}
        values={values}
        onChange={onChange}
        ariaLabel={ariaLabel}
      />
    );
  }
  if (descriptor.valueKind === 'weekdays' && descriptor.enumValues) {
    const tz = row.timeZoneCode?.trim() || 'UTC';
    return (
      <WeekdaysPicker
        options={descriptor.enumValues}
        values={values}
        onChange={onChange}
        timeZoneCode={tz}
        onTimeZoneChange={onTimeZoneChange ?? (() => undefined)}
        ariaLabel={ariaLabel}
      />
    );
  }
  if (descriptor.valueKind === 'country-codes') {
    return (
      <CountryCodesPicker
        values={values}
        onChange={onChange}
        ariaLabel={ariaLabel}
      />
    );
  }
  if (descriptor.valueKind === 'language-codes') {
    return (
      <CountryCodesPicker
        values={values}
        onChange={onChange}
        ariaLabel={ariaLabel}
        placeholder="Code (e.g. en, fr)"
      />
    );
  }
  return (
    <StringListPicker
      values={values}
      onChange={onChange}
      ariaLabel={ariaLabel}
    />
  );
};

export default ConditionRowValueSlot;
